/**
 * SystemAdmin — infrastructure status: OpenSearch indices, dramatiq queues,
 * MaxMind DB freshness. Renders the data from the system endpoints, falling
 * back to an EmptyState when a section has no data available.
 */

import { useQuery } from '@tanstack/react-query';
import { Database, Globe2, Layers } from 'lucide-react';

import { apiGet, ApiError } from '../../lib/api';
import { formatNumber, formatRelativeTime } from '../../lib/locale';
import { Card, CardHeader } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';

// ---------------------------------------------------------------------------
// Types — best-effort shapes; tolerated even if backend omits fields.
// ---------------------------------------------------------------------------

interface OpenSearchIndex {
  name: string;
  doc_count: number;
  size_bytes: number;
}

interface DramatiqQueue {
  name: string;
  depth: number;
  in_flight: number;
}

interface MaxMindStatus {
  database: string;
  last_updated: string | null;
  build_epoch: number | null;
}

interface SystemStatus {
  opensearch_indices: OpenSearchIndex[];
  dramatiq_queues: DramatiqQueue[];
  maxmind: MaxMindStatus[];
}

// ---------------------------------------------------------------------------

export function SystemAdmin(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'system-status'],
    queryFn: () => apiGet<{ data: SystemStatus }>('/system/status'),
    retry: false,
  });

  const is404 = query.error instanceof ApiError && query.error.status === 404;
  const status: Partial<SystemStatus> = query.data?.data ?? {};

  return (
    <div className="space-y-4 px-6 py-5">
      <header>
        <h1 className="text-lg font-semibold text-slate-900">Systeem</h1>
        <p className="mt-0.5 text-[12px] text-slate-500">
          Status van OpenSearch, dramatiq-queues en MaxMind GeoIP-database.
        </p>
      </header>

      {query.isLoading ? (
        <div className="flex justify-center px-6 py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <OpenSearchSection indices={status.opensearch_indices} placeholder={is404} />
          <DramatiqSection queues={status.dramatiq_queues} placeholder={is404} />
          <MaxMindSection entries={status.maxmind} placeholder={is404} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function OpenSearchSection({
  indices,
  placeholder,
}: {
  indices: OpenSearchIndex[] | undefined;
  placeholder: boolean;
}): JSX.Element {
  return (
    <Card>
      <CardHeader title="OpenSearch indices" subtitle="dmarc-records-* analytics store" />
      {placeholder || !indices ? (
        <EmptyState
          icon={<Database size={20} aria-hidden />}
          title="Geen OpenSearch-overzicht beschikbaar"
          description="Het indices-overzicht is nog niet beschikbaar. Inspecteer indices desgewenst via `curl -u … https://opensearch:9200/_cat/indices?v` op de host."
        />
      ) : indices.length === 0 ? (
        <EmptyState
          title="Nog geen indices"
          description="Zodra de eerste RUA rapporten zijn geïngest verschijnen hier `dmarc-records-{tenant}-YYYY.MM` indices."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Index</th>
                <th className="px-3 py-2 text-right">Documenten</th>
                <th className="px-3 py-2 text-right">Grootte</th>
              </tr>
            </thead>
            <tbody>
              {indices.map((index) => (
                <tr key={index.name} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-mono text-slate-900">{index.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatNumber(index.doc_count)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatBytes(index.size_bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function DramatiqSection({
  queues,
  placeholder,
}: {
  queues: DramatiqQueue[] | undefined;
  placeholder: boolean;
}): JSX.Element {
  return (
    <Card>
      <CardHeader title="Dramatiq queues" subtitle="Job queue depth per actor-group" />
      {placeholder || !queues ? (
        <EmptyState
          icon={<Layers size={20} aria-hidden />}
          title="Geen queue-overzicht beschikbaar"
          description="Het queue-overzicht is nog niet beschikbaar. Redis `LLEN dramatiq:queues:default` toont de huidige diepte van de default-queue."
        />
      ) : queues.length === 0 ? (
        <EmptyState title="Geen actieve queues" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Queue</th>
                <th className="px-3 py-2 text-right">Pending</th>
                <th className="px-3 py-2 text-right">In behandeling</th>
              </tr>
            </thead>
            <tbody>
              {queues.map((q) => (
                <tr key={q.name} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-mono text-slate-900">{q.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(q.depth)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(q.in_flight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function MaxMindSection({
  entries,
  placeholder,
}: {
  entries: MaxMindStatus[] | undefined;
  placeholder: boolean;
}): JSX.Element {
  return (
    <Card>
      <CardHeader
        title="MaxMind GeoIP-databases"
        subtitle="GeoLite2 ASN + City — wekelijkse update"
      />
      {placeholder || !entries ? (
        <EmptyState
          icon={<Globe2 size={20} aria-hidden />}
          title="Geen MaxMind freshness beschikbaar"
          description="De MaxMind freshness-informatie is nog niet beschikbaar. Kijk naar de `maxmind-updater` container logs voor het laatste download-tijdstip."
        />
      ) : entries.length === 0 ? (
        <EmptyState title="Geen MaxMind-databases geconfigureerd" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Database</th>
                <th className="px-3 py-2">Laatst geüpdatet</th>
                <th className="px-3 py-2">Build epoch</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.database} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-mono text-slate-900">{entry.database}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {entry.last_updated ? formatRelativeTime(entry.last_updated) : 'Onbekend'}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {entry.build_epoch ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatNumber(bytes)} B`;
  if (bytes < 1024 * 1024) return `${formatNumber(Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${formatNumber(Math.round(bytes / (1024 * 1024)))} MB`;
  return `${formatNumber(Math.round(bytes / (1024 * 1024 * 1024)))} GB`;
}
